import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import ThumbnailDefault from '~/components/elements/detail/thumbnail/ThumbnailDefault';
import ModuleDetailTopInformation from '~/components/elements/detail/modules/ModuleDetailTopInformation';
import ModuleProductDetailDescription from '~/components/elements/detail/modules/ModuleProductDetailDescription';
import ModuleDetailShoppingActions from '~/components/elements/detail/modules/ModuleDetailShoppingActions';
import ModuleProductDetailSpecification from '~/components/elements/detail/modules/ModuleProductDetailSpecification';
import ModuleProductDetailSharing from '~/components/elements/detail/modules/ModuleProductDetailSharing';
import ProductVariantSelector from '~/components/elements/detail/ProductVariantSelector';
import { useProductReviewFlow } from '~/components/shared/widgets/WidgetProductReview';

const QUICKVIEW_CSS = `
.ps-product--detail.ps-product--quickview {
    box-sizing: border-box;
    max-width: 100%;
    margin-bottom: 0;
}
.ps-product--detail.ps-product--quickview *,
.ps-product--detail.ps-product--quickview *::before,
.ps-product--detail.ps-product--quickview *::after {
    box-sizing: border-box;
}
.ps-product--detail.ps-product--quickview .ps-product__meta,
.ps-product--detail.ps-product--quickview .ps-product__rating {
    align-items: center;
}
.ps-product--detail.ps-product--quickview .ps-product__header {
    display: flex;
    flex-flow: row nowrap;
    gap: 30px; /* ersetzt padding-left */
    width: 100%;
}
.ps-product--detail.ps-product--quickview .ps-product__header > * {
    width: auto;
}
.ps-product--detail.ps-product--quickview .ps-product__header .ps-product__thumbnail {
    flex: 0 0 45%;
    max-width: 45%;
    min-width: 0;
}
.ps-product--detail.ps-product--quickview .ps-product__header .ps-product__info {
    flex: 1 1 0;
    min-width: 0;
    max-width: none;
    padding-left: 0;
}
.ps-product--detail.ps-product--quickview .ps-product__shopping {
    max-width: 100%;
}
.ps-product--detail.ps-product--quickview .ps-product__shopping .ps-product__btn-group {
    min-width: 0;
    gap: 10px;
}
.ps-product--detail.ps-product--quickview .ps-product__shopping .ps-product__btn-group .ps-btn {
    min-width: 0;
    padding-left: 20px;
    padding-right: 20px;
}
.ps-product--detail.ps-product--quickview .ps-product__shopping.extend > .ps-btn {
    width: 100%;
    max-width: 100%;
}
/* Entspricht @include media('<sm') im SCSS (hier als max-width: 767px angenommen) */
@media (max-width: 767px) {
    .ps-product--detail.ps-product--quickview .ps-product__header {
        flex-flow: column nowrap;
        /* Mobil untereinander: kleiner Abstand statt der 30px aus der Desktop-Ansicht */
        gap: 6px;
    }
    .ps-product--detail.ps-product--quickview .ps-product__header .ps-product__thumbnail,
    .ps-product--detail.ps-product--quickview .ps-product__header .ps-product__info {
        flex: 1 1 auto;
        width: 100%;
        max-width: 100%;
    }
    /* Kein zusätzlicher Abstand unter der Galerie (Theme setzt hier oft margin/padding) */
    .ps-product--detail.ps-product--quickview .ps-product__header .ps-product__thumbnail {
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
    }
    .ps-product--detail.ps-product--quickview .ps-product__header .ps-product__info {
        margin-top: 0 !important;
        padding-top: 0 !important;
    }
}

/*
 * Die fixierte Aktionsleiste (.ps-product__actions-mobile: position fixed, bottom 0,
 * 72px hoch, z-index 10003) gehört nur auf die Produktseite. In der Quickview würde sie
 * über dem Modal und der unteren Navigation liegen und den unteren Bereich verdecken.
 */
.ps-product--quickview .ps-product__actions-mobile {
    display: none !important;
}
/*
 * Der Wurzel-Container der Quickview bekommt per Script inline "overflow-y: auto" und
 * "max-height: Npx" gesetzt (erst nach dem Öffnen). Dadurch entsteht ein zweiter Scroll-
 * Container in der Box, dessen Höhe nachträglich springt. Gescrollt wird nur in der
 * äußeren Box (.pa-qv-box), deshalb hier neutralisieren (Stylesheet-!important schlägt
 * Inline-Styles).
 */
.ps-product--detail.ps-product--quickview {
    max-height: none !important;
    overflow: visible !important;
}
/*
 * Hauptbild von Anfang an quadratisch reservieren. Die Galerie bekommt ihre Höhe sonst
 * erst per Script (Swiper autoheight + Thumbnail-Wrapper "height: Npx" inline), sobald
 * das Bild geladen ist, und der Inhalt darunter rutscht nach unten.
 */
.ps-product--quickview .ps-gallery-main-image {
    aspect-ratio: 1 / 1;
}
/*
 * Markenlogo (.ps-product__meta) und Produktname (h1) im Info-Bereich der Quickview
 * auch mobil sichtbar halten.
 */
.ps-product--quickview .ps-product__info header .ps-product__meta {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    height: auto !important;
    position: static !important;
    margin: 0 0 6px 0 !important;
}
.ps-product--quickview .ps-product__info header .ps-product__meta img {
    display: block !important;
    width: auto !important;
    max-width: 100px !important;
    max-height: 32px !important;
    height: auto !important;
}
.ps-product--quickview .ps-product__info header h1 {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    height: auto !important;
    position: static !important;
    margin: 0 0 8px 0 !important;
    font-size: 18px !important;
    line-height: 1.3 !important;
    overflow-wrap: anywhere;
}
/* Produkttitel ist in der Quickview ein Link zur Produktseite */
.ps-product--quickview .ps-product__info header h1 a {
    color: inherit !important;
    text-decoration: none !important;
}
.ps-product--quickview .ps-product__info header h1 a:hover {
    text-decoration: underline !important;
}
`;

const ProductDetailQuickView = ({ product }) => {
    const sub_title = product.attributes.sub_title;

    const mainVariant = {
        attributes: {
            variant_value_1: product.attributes.variant_value_1,
            variant_value_2: product.attributes.variant_value_2,
            slug: product.attributes.slug,
            ...product.attributes,
        },
    };

    const variants = [
        mainVariant,
        ...(product?.attributes?.product_variants?.data || []),
    ];

    const searchParams = useSearchParams();
    const router = useRouter();

    // Startvariante direkt im ersten Render bestimmen (kein zweiter Render mit anderem Inhalt nach dem Öffnen)
    const [selectedVariant, setSelectedVariant] = useState(() => {
        const size = searchParams.get('variant_1');
        const color = searchParams.get('variant_2');
        if (size && color) {
            const match = variants.find(
                (variant) =>
                    variant.attributes.variant_value_1 === size &&
                    variant.attributes.variant_value_2 === color
            );
            if (match) return match;
        }
        return mainVariant;
    });

    // Bewertung: gleicher Ablauf wie auf der Produktseite (Login -> Berechtigung -> Modal mit productId/orderId)
    const { startReview, reviewModal } = useProductReviewFlow(product);

    const initialVariant = useMemo(() => {
        const size = searchParams.get('variant_1');
        const color = searchParams.get('variant_2');
        if (size && color) {
            return variants.find(
                (variant) =>
                    variant.attributes.variant_value_1 === size &&
                    variant.attributes.variant_value_2 === color
            );
        }
        return mainVariant;
    }, [searchParams, variants, mainVariant]);

    useEffect(() => {
        if (!selectedVariant) {
            setSelectedVariant(initialVariant);
        }
    }, [initialVariant, selectedVariant]);

    const enrichedProduct = useMemo(() => {
        const displayed = selectedVariant || product;
        const displayedAttributes = selectedVariant ? selectedVariant.attributes : product.attributes;
        return {
            ...product,
            selectedVariant,
            displayed,
            displayedAttributes,
        };
    }, [product, selectedVariant]);

    const handleVariantChange = useCallback((variant) => {
        setSelectedVariant(variant);
    }, []);

    const handleReviewClick = useCallback(() => {
        startReview();
    }, [startReview]);

    // Link zur Produktseite (Annahme: Route /product/[slug]) inkl. gewählter Variante
    const titleHref = useMemo(() => {
        const slug = product?.attributes?.slug;
        if (!slug) return null;

        const params = new URLSearchParams();
        const v1 = selectedVariant?.attributes?.variant_value_1;
        const v2 = selectedVariant?.attributes?.variant_value_2;
        if (v1 && v2) {
            params.set('variant_1', v1);
            params.set('variant_2', v2);
        }

        const query = params.toString();
        return `/product/${encodeURIComponent(slug)}${query ? `?${query}` : ''}`;
    }, [product, selectedVariant]);

    return (
        <>
            <div className="ps-product--detail ps-product--quickview">
                <div className="ps-product__header">
                    <ThumbnailDefault product={enrichedProduct} vertical={false} />
                    <div className="ps-product__info">
                        <ModuleDetailTopInformation
                            product={enrichedProduct}
                            selectedVariant={selectedVariant}
                            variants={variants}
                            onReviewClick={handleReviewClick}
                            titleHref={titleHref}
                        />
                        <ProductVariantSelector
                            product={product}
                            variants={variants}
                            selectedVariant={selectedVariant}
                            onVariantChange={handleVariantChange}
                        />
                        <ModuleProductDetailDescription product={product} />
                        <ModuleDetailShoppingActions
                            product={enrichedProduct}
                            selectedVariant={selectedVariant}
                            extended={true}
                        />
                        <ModuleProductDetailSpecification
                            product={enrichedProduct}
                            selectedVariant={selectedVariant}
                        />
                        <ModuleProductDetailSharing />
                    </div>
                </div>
            </div>

            {reviewModal}

            <style dangerouslySetInnerHTML={{ __html: QUICKVIEW_CSS }} />
        </>
    );
};

export default ProductDetailQuickView;